// src/configuration/WordTab.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  TextField,
  IconButton,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  LinearProgress,
  Alert,
  RadioGroup,
  FormControl,
  FormLabel,
  Radio,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Image as ImageIcon,
  Save as SaveIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { styled } from '@mui/system';
import useStore from '../config/store';
import { useTranslation } from 'react-i18next';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import api from '../config/api';

const DragDropArea = styled(Paper)(({ theme, isdragging }) => ({
  padding: theme.spacing(3),
  textAlign: 'center',
  border: '2px dashed #ccc',
  backgroundColor: isdragging === 'true' ? '#f0f0f0' : 'transparent',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: '#f8f9fa',
  },
}));

const LogoPreview = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid #ddd',
  borderRadius: theme.spacing(1),
  padding: theme.spacing(2),
  backgroundColor: '#f9f9f9',
  minHeight: 100,
  maxHeight: 150,
  overflow: 'hidden',
}));

// SectionCard FUERA del componente principal
const SectionCard = ({ section, title, icon, config, toggleSection, updateConfig, errors, uploading, uploadProgress, dragging, handleDrop, handleDragOver, handleDragEnter, handleDragLeave, handleFileUpload, clearImage, t }) => (
  <Card sx={{ mb: 3 }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        {icon}
        <Typography variant="h6" sx={{ ml: 1, flexGrow: 1 }}>
          {title}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={config[section].enabled}
              onChange={() => toggleSection(section)}
              color="primary"
            />
          }
          label={config[section].enabled ? t('configuration.enabled') : t('configuration.disabled')}
        />
      </Box>

      {config[section].enabled && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl component="fieldset" sx={{ mb: 2 }}>
              <FormLabel component="legend">{t('configuration.contentType')}</FormLabel>
              <RadioGroup
                value={config[section].type}
                onChange={(e) => updateConfig(section, 'type', e.target.value)}
                row
              >
                <FormControlLabel value="logo" control={<Radio />} label={t('configuration.logo')} />
                <FormControlLabel value="text" control={<Radio />} label={t('configuration.text')} />
              </RadioGroup>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{t('configuration.position')}</InputLabel>
              <Select
                value={config[section].position}
                onChange={(e) => updateConfig(section, 'position', e.target.value)}
                label={t('configuration.position')}
              >
                <MenuItem value="left">{t('configuration.left')}</MenuItem>
                <MenuItem value="center">{t('configuration.center')}</MenuItem>
                <MenuItem value="right">{t('configuration.right')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            {config[section].type === 'text' ? (
              <TextField
                fullWidth
                label={t('configuration.customText')}
                multiline
                rows={3}
                value={config[section].text}
                onChange={(e) => updateConfig(section, 'text', e.target.value)}
                error={!!errors[`${section}Text`]}
                helperText={errors[`${section}Text`]}
              />
            ) : (
              <Box>
                {!config[section].imgUrl ? (
                  <DragDropArea
                    isdragging={dragging[section].toString()}
                    onDrop={(e) => handleDrop(e, section)}
                    onDragOver={handleDragOver}
                    onDragEnter={(e) => handleDragEnter(e, section)}
                    onDragLeave={(e) => handleDragLeave(e, section)}
                  >
                    <input
                      accept="image/*"
                      id={`file-input-${section}`}
                      type="file"
                      style={{ display: 'none' }}
                      onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0], section)}
                    />
                    <label htmlFor={`file-input-${section}`}>
                      <Box sx={{ cursor: 'pointer' }}>
                        <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                        <Typography variant="body1">
                          {t('configuration.dragDropImage')}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {t('configuration.imageFormats')}
                        </Typography>
                      </Box>
                    </label>
                  </DragDropArea>
                ) : (
                  <LogoPreview>
                    <Box sx={{ position: 'relative', width: '100%' }}>
                      <img
                        src={config[section].imgUrl}
                        alt={`${section} logo`}
                        style={{ maxWidth: '100%', maxHeight: '120px', objectFit: 'contain' }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => clearImage(section)}
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          backgroundColor: 'error.main',
                          color: 'white',
                          '&:hover': { backgroundColor: 'error.dark' }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </LogoPreview>
                )}

                {uploading[section] && (
                  <Box sx={{ mt: 2 }}>
                    <LinearProgress variant="determinate" value={uploadProgress[section]} />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {t('configuration.uploading')}: {uploadProgress[section]}%
                    </Typography>
                  </Box>
                )}

                {errors[`${section}Logo`] && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {errors[`${section}Logo`]}
                  </Alert>
                )}
              </Box>
            )}
          </Grid>
        </Grid>
      )}
    </CardContent>
  </Card>
);

const WordTab = () => {
  const { t } = useTranslation();
  const { authToken, organization, project, showAlert, language } = useStore.getState();

  const [config, setConfig] = useState({
    header: { enabled: false, imgUrl: '', text: '', type: 'logo', position: 'left' },
    footer: { enabled: false, imgUrl: '', text: '', type: 'logo', position: 'left' },
  });
  const [dragging, setDragging] = useState({ header: false, footer: false });
  const [errors, setErrors] = useState({});
  const [uploading, setUploading] = useState({ header: false, footer: false });
  const [uploadProgress, setUploadProgress] = useState({ header: 0, footer: 0 });
  const [isGenerating, setIsGenerating] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);

  const loadConfiguration = async () => {
    try {
      setLoadingConfig(true);

      if (!organization || !project) {
        console.warn('Organization o Project no disponibles para cargar configuración');
        setLoadingConfig(false);
        return;
      }

      const response = await api.get(
        '/projects/word-configuration',
        {
          params: {
            organizationId: organization,
            projectId: project
          },
          headers: { Authorization: `Bearer ${authToken}` }
        }
      );

      if (response.data.response.status_response === 'OK') {
        const wordConfig = response.data.response.data;

        setConfig({
          header: wordConfig.header || {
            enabled: false,
            imgUrl: '',
            text: '',
            type: 'logo',
            position: 'left'
          },
          footer: wordConfig.footer || {
            enabled: false,
            imgUrl: '',
            text: '',
            type: 'logo',
            position: 'left'
          }
        });

        console.log('Configuración cargada:', wordConfig);
      } else {
        console.warn('No se pudo cargar la configuración:', response.data.response.message);
      }
    } catch (err) {
      console.error('Error cargando configuración:', err);
    } finally {
      setLoadingConfig(false);
    }
  };

  useEffect(() => {
    loadConfiguration();
  }, [organization, project]);

  const uploadImageToFirebase = async (file, section) => {
    try {
      setUploading(prev => ({ ...prev, [section]: true }));
      setUploadProgress(prev => ({ ...prev, [section]: 0 }));
      const storage = getStorage();
      const timestamp = Date.now();
      const fileName = `word-configuration/${section}-${timestamp}-${file.name}`;
      const storageRef = ref(storage, fileName);
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => ({ ...prev, [section]: Math.min(prev[section] + 10, 90) }));
      }, 100);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      clearInterval(progressInterval);
      setUploadProgress(prev => ({ ...prev, [section]: 100 }));
      setConfig(prev => ({
        ...prev,
        [section]: { ...prev[section], imgUrl: downloadURL },
      }));
      showAlert(
        t(`configuration.messages.${section}UploadSuccess`) ||
        `Imagen del ${section} subida exitosamente`,
        'success'
      );
    } catch (error) {
      console.error(`Error uploading ${section} image:`, error);
      showAlert(
        t('configuration.messages.uploadError') || `Error al subir imagen: ${error.message}`,
        'error'
      );
    } finally {
      setUploading(prev => ({ ...prev, [section]: false }));
    }
  };

  const handleFileUpload = async (file, section) => {
    if (file.size > 2 * 1024 * 1024) {
      showAlert(t('configuration.messages.logoSizeError'), 'warning');
      return;
    }
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      showAlert(t('configuration.messages.logoFormatError'), 'warning');
      return;
    }
    await uploadImageToFirebase(file, section);
  };

  const handleDrop = (e, section) => {
    e.preventDefault();
    setDragging(prev => ({ ...prev, [section]: false }));
    const files = e.dataTransfer.files;
    if (files.length > 0) handleFileUpload(files[0], section);
  };

  const handleDragOver = (e) => e.preventDefault();
  const handleDragEnter = (e, section) => {
    e.preventDefault();
    setDragging(prev => ({ ...prev, [section]: true }));
  };
  const handleDragLeave = (e, section) => {
    e.preventDefault();
    setDragging(prev => ({ ...prev, [section]: false }));
  };

  const validateConfiguration = () => {
    const newErrors = {};
    ['header', 'footer'].forEach(section => {
      const cfg = config[section];
      if (cfg.enabled) {
        if (cfg.type === 'text' && !cfg.text.trim()) {
          newErrors[`${section}Text`] = t('configuration.validation.textRequired');
        }
        if (cfg.type === 'logo' && !cfg.imgUrl) {
          newErrors[`${section}Logo`] = t('configuration.validation.logoRequired');
        }
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isConfigValid = useMemo(() => {
    let isValid = true;
    ['header', 'footer'].forEach(section => {
      const cfg = config[section];
      if (cfg.enabled) {
        if (cfg.type === 'text' && !cfg.text.trim()) {
          isValid = false;
        }
        if (cfg.type === 'logo' && !cfg.imgUrl) {
          isValid = false;
        }
      }
    });
    return isValid;
  }, [config]);

  const handleSave = async () => {
    if (!validateConfiguration()) return;

    try {
      setSavingConfig(true);

      if (!organization || !project) {
        showAlert('No se puede guardar: organización o proyecto no disponibles', 'error');
        return;
      }

      const response = await api.put(
        '/projects/word-configuration',
        {
          header: config.header,
          footer: config.footer
        },
        {
          params: {
            organizationId: organization,
            projectId: project
          },
          headers: { Authorization: `Bearer ${authToken}` }
        }
      );

      if (response.data.response.status_response === 'OK') {
        showAlert(t('configuration.messages.saveSuccess'), 'success');
        console.log('Configuración guardada exitosamente:', response.data.response.data);
      } else {
        showAlert('Error al guardar configuración: ' + response.data.response.message, 'error');
      }
    } catch (err) {
      console.error('Error guardando configuración:', err);
      showAlert('Error al guardar la configuración', 'error');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleDownloadExample = async () => {
    if (!validateConfiguration()) return;
    setIsGenerating(true);
    try {
      const response = await api.post(
        '/projects/generate-word-preview',
        { header: config.header, footer: config.footer, language },
        {
          headers: { Authorization: `Bearer ${authToken}` },
          responseType: 'blob',
        }
      );
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ejemplo-configuracion-${Date.now()}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showAlert(t('configuration.messages.downloadSuccess'), 'success');
    } catch (error) {
      console.error('Error al descargar ejemplo:', error);
      showAlert(t('configuration.messages.downloadError'), 'error');
    } finally {
      setIsGenerating(false);
    }
  };

  const toggleSection = (section) => {
    setConfig(prev => ({
      ...prev,
      [section]: { ...prev[section], enabled: !prev[section].enabled }
    }));
  };

  const updateConfig = (section, field, value) => {
    setConfig(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: value }
    }));
  };

  const clearImage = (section) => {
    setConfig(prev => ({
      ...prev,
      [section]: { ...prev[section], imgUrl: '' }
    }));
  };

  return (
    <Box>
      {loadingConfig && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>
            {t('configuration.messages.loadingConfig') || 'Cargando configuración...'}
          </Typography>
        </Box>
      )}

      {!loadingConfig && (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="body1" color="textSecondary" paragraph>
              {t('configuration.description')}
            </Typography>
          </Paper>

          <SectionCard
            section="header"
            title={t('configuration.header')}
            icon={<ImageIcon color="primary" />}
            config={config}
            toggleSection={toggleSection}
            updateConfig={updateConfig}
            errors={errors}
            uploading={uploading}
            uploadProgress={uploadProgress}
            dragging={dragging}
            handleDrop={handleDrop}
            handleDragOver={handleDragOver}
            handleDragEnter={handleDragEnter}
            handleDragLeave={handleDragLeave}
            handleFileUpload={handleFileUpload}
            clearImage={clearImage}
            t={t}
          />

          <SectionCard
            section="footer"
            title={t('configuration.footer')}
            icon={<DescriptionIcon color="primary" />}
            config={config}
            toggleSection={toggleSection}
            updateConfig={updateConfig}
            errors={errors}
            uploading={uploading}
            uploadProgress={uploadProgress}
            dragging={dragging}
            handleDrop={handleDrop}
            handleDragOver={handleDragOver}
            handleDragEnter={handleDragEnter}
            handleDragLeave={handleDragLeave}
            handleFileUpload={handleFileUpload}
            clearImage={clearImage}
            t={t}
          />

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4 }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!isConfigValid || savingConfig}
              startIcon={savingConfig ? <CircularProgress size={20} /> : <SaveIcon />}
              size="large"
            >
              {savingConfig ? t('configuration.saving') || 'Guardando...' : t('configuration.save')}
            </Button>

            <Button
              variant="outlined"
              onClick={handleDownloadExample}
              disabled={!isConfigValid || isGenerating}
              startIcon={isGenerating ? <CircularProgress size={20} /> : <DownloadIcon />}
              size="large"
            >
              {isGenerating ? t('configuration.generating') : t('configuration.downloadExample')}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default WordTab;